import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, ChevronRight, GraduationCap } from 'lucide-react';
import { getTeacherCourses, getTeacherCourseStudents } from '../../api/teacher';
import Dropdown from '../../components/Dropdown/Dropdown';
import SearchInput from '../../components/SearchInput/SearchInput';
import FilterSheet from '../../components/FilterSheet/FilterSheet';
import PageHead from '../../components/PageHead/PageHead';
import ProgressBar from '../../components/ProgressBar/ProgressBar';
import Avatar from '../../components/Avatar/Avatar';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import TableCard from '../../components/TableCard/TableCard';
import Pagination from '../../components/Pagination/Pagination';
import CourseSelect from './CourseSelect';
import useTeacherCourse from './useTeacherCourse';
import { failure, failureText } from './apiError';
import { formatPercent, formatRelative } from '../../utils/datetime';
import { useI18n } from '../../i18n/I18nContext';
import { pluralSuffix } from './pluralKey';
import LoadingState from '../../components/LoadingState/LoadingState';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import './TeacherDashboard.css';

const DAY_MS = 1000 * 60 * 60 * 24;
const SEARCH_PAUSE_MS = 400;
const PAGE_SIZE = 10;
const DEFAULT_SORT = 'at-risk'; // at-risk students first, per the spec

/**
 * When the student was last here. Inside the last day it says how long ago ("2 soat oldin");
 * after that it counts days, weeks and months, which is what a teacher scanning for quiet
 * students reads at a glance.
 */
function lastActiveLabel(dateStr, t, lang) {
  if (!dateStr) return t('teacher.dashboard.never');
  const diffDays = Math.floor((Date.now() - new Date(dateStr).getTime()) / DAY_MS);
  if (diffDays <= 0) return formatRelative(dateStr, { lang });
  if (diffDays === 1) return t('teacher.dashboard.yesterday');
  // "1 week ago" / "2 weeks ago": the unit agrees with the number in every language (pluralSuffix)
  const ago = (unit, count) => t(`teacher.dashboard.${unit}Ago${pluralSuffix(lang, count)}`, { count });
  if (diffDays < 7) return ago('days', diffDays);
  if (diffDays < 30) return ago('weeks', Math.floor(diffDays / 7));
  return ago('months', Math.floor(diffDays / 30));
}

/**
 * Map the backend's SHOUTING_CASE enrollment status to a translated, sentence-case
 * badge label (never the raw enum).
 */
function formatEnrollmentStatus(status, t) {
  switch (status) {
    case 'ACTIVE': return t('teacher.dashboard.statusActive');
    case 'COMPLETED': return t('teacher.dashboard.statusCompleted');
    case 'INACTIVE': return t('teacher.dashboard.statusInactive');
    case 'AT_RISK': return t('teacher.dashboard.statusAtRisk');
    default: return status || '-';
  }
}

const STATUS_TONE = { ACTIVE: 'success', COMPLETED: 'info', INACTIVE: 'neutral', AT_RISK: 'warning' };

/**
 * Returns true if the student is at risk. The API decides (inactive 7+ days or struggling on
 * quizzes, and not finished); a response without the flag falls back to inactivity alone.
 */
function isAtRisk(student) {
  if (typeof student.atRisk === 'boolean') return student.atRisk;
  if (!student.lastActivityAt) return true;
  const lastActive = new Date(student.lastActivityAt);
  const diffDays = (new Date() - lastActive) / DAY_MS;
  return diffDays >= 7;
}

/**
 * Sort students client-side. Backend returns data in insertion order.
 */
function sortStudents(list, sortBy) {
  const sorted = [...list];
  switch (sortBy) {
    case 'at-risk':
      return sorted.sort((a, b) => {
        const aRisk = isAtRisk(a) ? 0 : 1;
        const bRisk = isAtRisk(b) ? 0 : 1;
        if (aRisk !== bRisk) return aRisk - bRisk;
        return (a.name || '').localeCompare(b.name || '');
      });
    case 'progress-asc':
      return sorted.sort((a, b) => (a.progress || 0) - (b.progress || 0));
    case 'last-active':
      return sorted.sort((a, b) => {
        const aDate = a.lastActivityAt ? new Date(a.lastActivityAt) : new Date(0);
        const bDate = b.lastActivityAt ? new Date(b.lastActivityAt) : new Date(0);
        return bDate - aDate;
      });
    case 'alphabetical':
      return sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    default:
      return sorted;
  }
}

function TeacherDashboard() {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [courses, setCourses] = useState([]);
  const [selectedCourse, selectCourse] = useTeacherCourse(courses);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState(DEFAULT_SORT);
  const [notAssigned, setNotAssigned] = useState(false);
  // The list for the chosen course has come back (or failed) at least once. Until then the table
  // is a skeleton, so "no students" is never said before the answer is in.
  const [studentsReady, setStudentsReady] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 0
  });

  useEffect(() => {
    fetchCourses();
    // Runs once on mount — fetchCourses is stable for the component's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStudents = useCallback(async (page = 1) => {
    if (!selectedCourse) return;
    try {
      setLoading(true);
      const result = await getTeacherCourseStudents(selectedCourse, {
        page,
        limit: pagination.limit,
        search: search || undefined,
        status: statusFilter || undefined
      });

      // Backend returns { data: StudentListItemDto[], meta: { total, limit, offset, courseId } }
      const rawList = Array.isArray(result) ? result : result.data || [];
      setStudents(rawList);
      setPagination(prev => ({
        ...prev,
        page,
        total: result.meta?.total || 0,
        totalPages: result.meta?.totalPages || Math.ceil((result.meta?.total || 0) / pagination.limit)
      }));
      setError(null);
      setNotAssigned(false);
    } catch (err) {
      setStudents([]);
      if (err?.code === 403) {
        setNotAssigned(true);
        setError(null);
      } else {
        setNotAssigned(false);
        setError(failure(err, t('teacher.dashboard.failedToLoadStudents')));
      }
    } finally {
      setLoading(false);
      setStudentsReady(true);
    }
  }, [selectedCourse, search, statusFilter, pagination.limit, t]);

  // The effect below always calls the newest fetchStudents (the one that sees the current search,
  // status and course), whatever render scheduled it.
  const fetchStudentsRef = useRef(fetchStudents);
  fetchStudentsRef.current = fetchStudents;

  // Typing waits for a pause (every keystroke restarts the wait: one request, not one per letter);
  // opening the page, picking a course or a status loads at once.
  const lastSearchRef = useRef(search);
  useEffect(() => {
    if (!selectedCourse) return undefined;
    const typed = lastSearchRef.current !== search;
    lastSearchRef.current = search;
    if (!typed) {
      fetchStudentsRef.current(1);
      return undefined;
    }
    const timer = setTimeout(() => fetchStudentsRef.current(1), SEARCH_PAUSE_MS);
    return () => clearTimeout(timer);
  }, [selectedCourse, search, statusFilter]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const result = await getTeacherCourses();
      const coursesList = Array.isArray(result) ? result : result.data || [];
      setCourses(coursesList);
      setError(null);
    } catch (err) {
      setError(failure(err, t('teacher.dashboard.failedToLoadCourses')));
    } finally {
      setLoading(false);
    }
  };

  // courseId also travels in the query string (not just location.state) so the
  // student detail page survives a refresh/bookmark/shared link.
  const studentLocation = (studentId) => ({
    pathname: `/teacher/students/${studentId}`,
    search: `?courseId=${encodeURIComponent(selectedCourse)}`,
  });

  const handleViewStudent = (studentId) => {
    navigate(studentLocation(studentId), { state: { courseId: selectedCourse } });
  };

  const handleNextPage = () => {
    if (pagination.page < pagination.totalPages) {
      fetchStudents(pagination.page + 1);
    }
  };

  const handlePreviousPage = () => {
    if (pagination.page > 1) {
      fetchStudents(pagination.page - 1);
    }
  };

  const handleCourseChange = (courseId) => {
    // The course field calls onChange(id) directly — not a DOM event. The pick is remembered
    // (useTeacherCourse), so the metrics and Q&A pages open on the same course.
    if (courseId === selectedCourse) return;
    selectCourse(courseId);
    setStudents([]);
    setStudentsReady(false);
    setPagination({ page: 1, limit: PAGE_SIZE, total: 0, totalPages: 0 });
  };

  const handleRetry = () => {
    if (courses.length === 0) fetchCourses();
    else fetchStudents(pagination.page || 1);
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
  };

  // Apply client-side sort on top of server-filtered data
  const displayedStudents = sortStudents(students, sortBy);
  const hasFilters = Boolean(search || statusFilter);
  // What the "Filtrlar" sheet holds on a phone: the status and the order, when they are not the defaults
  const sheetFilterCount = (statusFilter ? 1 : 0) + (sortBy !== DEFAULT_SORT ? 1 : 0);
  const resetSheetFilters = () => {
    setStatusFilter('');
    setSortBy(DEFAULT_SORT);
  };

  const head = (
    <PageHead
      icon={<GraduationCap />}
      title={t('teacher.dashboard.title')}
      description={t('teacher.dashboard.subtitle')}
      actions={courses.length > 0 ? (
        <CourseSelect
          courses={courses}
          value={selectedCourse}
          onChange={handleCourseChange}
          ariaLabel={t('teacher.dashboard.selectCourse')}
          placeholder={t('teacher.dashboard.selectCoursePlaceholder')}
          getLabel={(course) => {
            const count = course.enrollmentCount || course._count?.enrollments || 0;
            return `${course.title || course.name} (${t(`teacher.dashboard.coursesLabel${pluralSuffix(lang, count)}`, { count })})`;
          }}
        />
      ) : null}
    />
  );

  if (loading && !courses.length) {
    return (
      <div className="teacher-dashboard">
        {head}
        <LoadingState message={t('teacher.dashboard.loadingCourses')} skeleton="table" rows={6} />
      </div>
    );
  }

  const loadFailed = Boolean(error) && students.length === 0;
  // A course nobody is enrolled in: no search, status or sort to offer over an empty list
  const courseIsEmpty = studentsReady && !loading && students.length === 0 && !hasFilters;
  const showToolbar = studentsReady && !notAssigned && !loadFailed && !courseIsEmpty;

  const results = () => {
    if (notAssigned) {
      return (
        <PageEmptyState
          variant="forbidden"
          title={t('teacher.dashboard.notAssignedTitle')}
          description={t('teacher.dashboard.notAssigned')}
        />
      );
    }
    if (!studentsReady || (loading && students.length === 0)) {
      return <LoadingState message={t('teacher.dashboard.loadingStudents')} skeleton="table" rows={6} />;
    }
    if (displayedStudents.length === 0) {
      // A failed load is an error, not "nobody here"
      if (error) return <PageEmptyState variant="error" title={error.title} description={error.detail} onRetry={handleRetry} />;
      return hasFilters ? (
        <PageEmptyState
          variant="search"
          title={t('teacher.dashboard.noResultsTitle')}
          description={t('teacher.dashboard.noResultsDesc')}
          actionLabel={t('teacher.dashboard.clearFilters')}
          onAction={clearFilters}
        />
      ) : (
        <PageEmptyState
          variant="students"
          title={t('teacher.dashboard.emptyStudents')}
          description={t('teacher.dashboard.emptyStudentsDesc')}
        />
      );
    }
    return (
      <TableCard
        aria-label={t('teacher.dashboard.tableAria')}
        footer={pagination.totalPages > 1 ? (
          <Pagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            limit={pagination.limit}
            onPrev={handlePreviousPage}
            onNext={handleNextPage}
          />
        ) : null}
      >
        <table className="ui-table ui-table--stack td-table">
          <thead>
            <tr>
              <th>{t('teacher.dashboard.colName')}</th>
              <th>{t('teacher.dashboard.colStatus')}</th>
              <th>{t('teacher.dashboard.colProgress')}</th>
              <th>{t('teacher.dashboard.colLastActive')}</th>
              <th className="ui-num">{t('teacher.dashboard.colQuizAverage')}</th>
              <th><span className="visually-hidden">{t('teacher.dashboard.colAction')}</span></th>
            </tr>
          </thead>
          <tbody>
            {displayedStudents.map((student) => {
              const atRisk = isAtRisk(student);
              // Backend uses userId as the student identifier in StudentListItemDto
              const studentId = student.userId || student.id;
              const progress = Math.min(100, Math.max(0, Number(student.progress) || 0));
              return (
                <tr key={studentId} className="is-clickable" onClick={() => handleViewStudent(studentId)}>
                  <td>
                    <div className="ui-cell-person">
                      <Avatar name={student.name} id={studentId} />
                      <div className="ui-cell-person__text">
                        <div className="td-name-row">
                          {/* A real link: the keyboard, a middle click and "open in a new tab" reach the student too */}
                          <Link
                            className="ui-cell-person__name td-name-link"
                            to={studentLocation(studentId)}
                            state={{ courseId: selectedCourse }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {student.name}
                          </Link>
                          {atRisk && student.enrollmentStatus !== 'AT_RISK' && (
                            <span className="at-risk-badge ds-badge ds-badge-warning">{t('teacher.dashboard.atRiskBadge')}</span>
                          )}
                        </div>
                        <span className="ui-cell-person__sub">{student.email}</span>
                      </div>
                    </div>
                  </td>
                  <td data-label={t('teacher.dashboard.colStatus')}>
                    <StatusBadge tone={STATUS_TONE[student.enrollmentStatus] || 'neutral'}>
                      {formatEnrollmentStatus(student.enrollmentStatus, t)}
                    </StatusBadge>
                  </td>
                  <td data-label={t('teacher.dashboard.colProgress')}>
                    <div className="td-progress">
                      <ProgressBar
                        value={progress}
                        label={`${student.name}: ${t('teacher.dashboard.colProgress')}`}
                        className="td-progress__bar"
                      />
                      <span className="td-progress__text">{formatPercent(progress)}</span>
                    </div>
                  </td>
                  <td data-label={t('teacher.dashboard.colLastActive')}>
                    {lastActiveLabel(student.lastActivityAt, t, lang)}
                  </td>
                  <td className="ui-num" data-label={t('teacher.dashboard.colQuizAverage')}>
                    {student.avgQuizScore != null
                      ? formatPercent(student.avgQuizScore)
                      : '—'}
                  </td>
                  <td className="ui-row-actions-cell">
                    <div className="ui-row-actions">
                      <button
                        type="button"
                        className="ui-icon-btn"
                        onClick={(e) => { e.stopPropagation(); handleViewStudent(studentId); }}
                        aria-label={t('teacher.dashboard.view')}
                        title={t('teacher.dashboard.view')}
                      >
                        <ChevronRight aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableCard>
    );
  };

  return (
    <div className="teacher-dashboard">
      {head}

      {error && students.length > 0 && (
        <div className="ui-alert td-alert" role="alert">
          <AlertCircle aria-hidden="true" />
          <p>{failureText(error)}</p>
          <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={handleRetry}>
            {t('ui.retryAgain')}
          </button>
        </div>
      )}

      {courses.length === 0 ? (
        error ? (
          <PageEmptyState variant="error" title={error.title} description={error.detail} onRetry={handleRetry} />
        ) : (
          <PageEmptyState
            variant="students"
            kind="courses"
            title={t('teacher.dashboard.notAssignedTitle')}
            description={t('teacher.dashboard.notAssigned')}
          />
        )
      ) : (
        <>
          {/* Until the first answer is in, the toolbar is bones in its own shape, so nothing jumps when it arrives */}
          {!studentsReady && (
            <div className="td-toolbar td-toolbar--skel" aria-hidden="true">
              <div className="ui-skel td-skel-search" />
              <div className="ui-skel td-skel-select" />
              <div className="ui-skel td-skel-select" />
            </div>
          )}

          {/* Search, and the status and order (one "Filtrlar" button on a phone) */}
          {showToolbar && (
            <div className="td-toolbar">
              <SearchInput
                className="td-toolbar__search"
                value={search}
                onChange={setSearch}
                placeholder={t('teacher.dashboard.searchPlaceholder')}
                aria-label={t('teacher.dashboard.searchAria')}
              />
              <FilterSheet count={sheetFilterCount} onClear={resetSheetFilters}>
                {/* The labels show only inside the phone sheet, where a bare value would not say what it filters */}
                <div className="td-filter">
                  <span className="td-filter__label ui-label">{t('teacher.dashboard.colStatus')}</span>
                  <Dropdown
                    size="sm"
                    value={statusFilter}
                    onChange={(val) => setStatusFilter(val)}
                    options={[
                      { value: '', label: t('teacher.dashboard.filterAllStatuses') },
                      { value: 'ACTIVE', label: t('teacher.dashboard.filterActive') },
                      { value: 'COMPLETED', label: t('teacher.dashboard.filterCompleted') },
                      { value: 'INACTIVE', label: t('teacher.dashboard.filterInactive') },
                      { value: 'AT_RISK', label: t('teacher.dashboard.filterAtRisk') },
                    ]}
                    placeholder={t('teacher.dashboard.allStatuses')}
                    ariaLabel={t('teacher.dashboard.filterStatusAria')}
                  />
                </div>
                <div className="td-filter">
                  <span className="td-filter__label ui-label">{t('teacher.dashboard.sortLabel')}</span>
                  <Dropdown
                    size="sm"
                    value={sortBy}
                    onChange={(val) => setSortBy(val)}
                    options={[
                      { value: 'at-risk', label: t('teacher.dashboard.sortAtRisk') },
                      { value: 'progress-asc', label: t('teacher.dashboard.sortProgressAsc') },
                      { value: 'last-active', label: t('teacher.dashboard.sortLastActive') },
                      { value: 'alphabetical', label: t('teacher.dashboard.sortAlphabetical') },
                    ]}
                    ariaLabel={t('teacher.dashboard.sortAria')}
                  />
                </div>
              </FilterSheet>
            </div>
          )}

          {results()}
        </>
      )}
    </div>
  );
}

export default TeacherDashboard;
